import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, Switch } from 'react-native';
import { XStack, YStack, Slider } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';

interface ComponentElement {
  id: string;
  type: 'text' | 'header' | 'image' | 'input' | 'button' | 'container' | 'divider';
  x: number;
  y: number;
  width: number;
  height: number;
  properties: {
    text?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    borderRadius?: number;
    padding?: number;
    margin?: number;
    alignment?: 'left' | 'center' | 'right';
    dataBinding?: string;
    placeholder?: string;
    imageSource?: string;
    borderWidth?: number;
    borderColor?: string;
    height?: number;
  };
}

interface PropertiesPanelProps {
  selectedElement: ComponentElement | null;
  onPropertyUpdate: (property: string, value: any) => void;
  onPositionUpdate: (x: number, y: number) => void;
  onSizeUpdate: (width: number, height: number) => void;
  theme: boolean;
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElement,
  onPropertyUpdate,
  onPositionUpdate,
  onSizeUpdate,
  theme
}) => {
  const [activeSection, setActiveSection] = useState<'general' | 'style' | 'layout' | 'advanced'>('general');

  if (!selectedElement) {
    return (
      <View style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}>
        <MaterialIcons
          name="tune"
          size={48}
          color={theme ? '#cccccc' : '#666666'}
        />
        <Text style={{
          fontSize: 16,
          color: theme ? '#666666' : '#cccccc',
          textAlign: 'center',
          marginTop: 12,
          fontWeight: '600'
        }}>
          No Element Selected
        </Text>
        <Text style={{
          fontSize: 12,
          color: theme ? '#999999' : '#888888',
          textAlign: 'center',
          marginTop: 4
        }}>
          Select an element on the canvas to edit its properties
        </Text>
      </View>
    );
  }

  const sections = [
    { key: 'general', label: 'General', icon: 'settings' },
    { key: 'style', label: 'Style', icon: 'palette' },
    { key: 'layout', label: 'Layout', icon: 'view-quilt' },
    { key: 'advanced', label: 'Advanced', icon: 'tune' }
  ];

  const renderColorPicker = (label: string, value: string | undefined, onChange: (color: string) => void) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={{
        fontSize: 14,
        fontWeight: '600',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 8
      }}>
        {label}
      </Text>
      <XStack alignItems="center" gap="$2">
        <TouchableOpacity
          style={{
            width: 32,
            height: 32,
            backgroundColor: value || '#000000',
            borderRadius: 6,
            borderWidth: 2,
            borderColor: theme ? '#e0e0e0' : '#444444'
          }}
          onPress={() => {
            // Color picker would open here
            // For now, cycle through common colors
            const colors = ['#000000', '#ffffff', '#2196F3', '#4CAF50', '#FF9800', '#f44336', '#9C27B0'];
            const currentIndex = colors.indexOf(value || '#000000');
            const nextIndex = (currentIndex + 1) % colors.length;
            onChange(colors[nextIndex]);
          }}
        />
        <TextInput
          value={value || '#000000'}
          onChangeText={onChange}
          style={{
            flex: 1,
            padding: 8,
            borderWidth: 1,
            borderColor: theme ? '#e0e0e0' : '#444444',
            borderRadius: 6,
            backgroundColor: theme ? '#ffffff' : '#333333',
            color: theme ? '#333333' : '#ffffff',
            fontSize: 14
          }}
          placeholder="Enter color code"
          placeholderTextColor={theme ? '#999999' : '#666666'}
        />
      </XStack>
    </View>
  );

  const renderSlider = (
    label: string,
    value: number | undefined,
    min: number,
    max: number,
    step: number,
    onChange: (value: number) => void,
    unit: string = ''
  ) => (
    <View style={{ marginBottom: 16 }}>
      <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
        <Text style={{
          fontSize: 14,
          fontWeight: '600',
          color: theme ? '#333333' : '#ffffff'
        }}>
          {label}
        </Text>
        <Text style={{
          fontSize: 12,
          color: theme ? '#666666' : '#cccccc',
          fontWeight: '600'
        }}>
          {value || 0}{unit}
        </Text>
      </XStack>
      <Slider
        size="$2"
        width="100%"
        value={[value || min]}
        min={min}
        max={max}
        step={step}
        onValueChange={(vals) => onChange(vals[0])}
        backgroundColor={theme ? '#f0f0f0' : '#333333'}
      >
        <Slider.Track backgroundColor={theme ? '#e0e0e0' : '#555555'}>
          <Slider.TrackActive backgroundColor="#2196F3" />
        </Slider.Track>
        <Slider.Thumb
          size="$1"
          index={0}
          backgroundColor="#2196F3"
          borderColor="#ffffff"
          borderWidth={2}
        />
      </Slider>
    </View>
  );

  const renderTextInput = (
    label: string,
    value: string | undefined,
    onChange: (value: string) => void,
    placeholder?: string,
    multiline?: boolean
  ) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={{
        fontSize: 14,
        fontWeight: '600',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 8
      }}>
        {label}
      </Text>
      <TextInput
        value={value || ''}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={theme ? '#999999' : '#666666'}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
        style={{
          padding: 12,
          borderWidth: 1,
          borderColor: theme ? '#e0e0e0' : '#444444',
          borderRadius: 8,
          backgroundColor: theme ? '#ffffff' : '#333333',
          color: theme ? '#333333' : '#ffffff',
          fontSize: 14,
          textAlignVertical: multiline ? 'top' : 'center'
        }}
      />
    </View>
  );

  const renderNumberInput = (
    label: string,
    value: number | undefined,
    onChange: (value: number) => void,
    unit: string = '',
    min?: number,
    max?: number
  ) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={{
        fontSize: 14,
        fontWeight: '600',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 8
      }}>
        {label}
      </Text>
      <XStack alignItems="center" gap="$2">
        <TextInput
          value={String(value || 0)}
          onChangeText={(text) => {
            const num = parseFloat(text) || 0;
            if (min !== undefined && num < min) return;
            if (max !== undefined && num > max) return;
            onChange(num);
          }}
          keyboardType="numeric"
          style={{
            flex: 1,
            padding: 8,
            borderWidth: 1,
            borderColor: theme ? '#e0e0e0' : '#444444',
            borderRadius: 6,
            backgroundColor: theme ? '#ffffff' : '#333333',
            color: theme ? '#333333' : '#ffffff',
            fontSize: 14,
            textAlign: 'center'
          }}
        />
        {unit && (
          <Text style={{
            fontSize: 12,
            color: theme ? '#666666' : '#cccccc',
            minWidth: 20
          }}>
            {unit}
          </Text>
        )}
      </XStack>
    </View>
  );

  const renderPicker = (
    label: string,
    value: string | undefined,
    options: { label: string; value: string }[],
    onChange: (value: string) => void
  ) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={{
        fontSize: 14,
        fontWeight: '600',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 8
      }}>
        {label}
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <XStack gap="$2">
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              onPress={() => onChange(option.value)}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: value === option.value ? '#2196F3' : (theme ? '#e0e0e0' : '#444444'),
                backgroundColor: value === option.value
                  ? (theme ? '#e3f2fd' : '#1a237e')
                  : (theme ? '#ffffff' : '#333333')
              }}
            >
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: value === option.value
                  ? '#2196F3'
                  : (theme ? '#333333' : '#ffffff')
              }}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </XStack>
      </ScrollView>
    </View>
  );

  const renderGeneralProperties = () => {
    switch (selectedElement.type) {
      case 'text':
      case 'header':
        return (
          <>
            {renderTextInput(
              'Text Content',
              selectedElement.properties.text,
              (value) => onPropertyUpdate('text', value),
              'Enter text content...'
            )}
            {renderNumberInput(
              'Font Size',
              selectedElement.properties.fontSize,
              (value) => onPropertyUpdate('fontSize', value),
              'px',
              8,
              72
            )}
            {renderPicker(
              'Font Weight',
              selectedElement.properties.fontWeight,
              [
                { label: 'Normal', value: 'normal' },
                { label: 'Bold', value: 'bold' },
                { label: 'Light', value: '300' },
                { label: 'Medium', value: '500' },
                { label: 'Semi Bold', value: '600' }
              ],
              (value) => onPropertyUpdate('fontWeight', value)
            )}
            {renderPicker(
              'Text Alignment',
              selectedElement.properties.alignment,
              [
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' },
                { label: 'Right', value: 'right' }
              ],
              (value) => onPropertyUpdate('alignment', value)
            )}
          </>
        );

      case 'input':
        return (
          <>
            {renderTextInput(
              'Placeholder Text',
              selectedElement.properties.placeholder,
              (value) => onPropertyUpdate('placeholder', value),
              'Enter placeholder text...'
            )}
            {renderNumberInput(
              'Font Size',
              selectedElement.properties.fontSize,
              (value) => onPropertyUpdate('fontSize', value),
              'px',
              8,
              32
            )}
          </>
        );

      case 'button':
        return (
          <>
            {renderTextInput(
              'Button Text',
              selectedElement.properties.text,
              (value) => onPropertyUpdate('text', value),
              'Enter button text...'
            )}
            {renderNumberInput(
              'Font Size',
              selectedElement.properties.fontSize,
              (value) => onPropertyUpdate('fontSize', value),
              'px',
              8,
              32
            )}
          </>
        );

      case 'image':
        return (
          <>
            {renderTextInput(
              'Image Source URL',
              selectedElement.properties.imageSource,
              (value) => onPropertyUpdate('imageSource', value),
              'Enter image URL...'
            )}
          </>
        );

      default:
        return (
          <Text style={{
            fontSize: 12,
            color: theme ? '#666666' : '#cccccc',
            fontStyle: 'italic'
          }}>
            No general properties available for this element type.
          </Text>
        );
    }
  };

  const renderStyleProperties = () => (
    <>
      {renderColorPicker(
        'Text Color',
        selectedElement.properties.color,
        (value) => onPropertyUpdate('color', value)
      )}
      {renderColorPicker(
        'Background Color',
        selectedElement.properties.backgroundColor,
        (value) => onPropertyUpdate('backgroundColor', value)
      )}
      {renderColorPicker(
        'Border Color',
        selectedElement.properties.borderColor,
        (value) => onPropertyUpdate('borderColor', value)
      )}
      {renderSlider(
        'Border Width',
        selectedElement.properties.borderWidth,
        0,
        10,
        1,
        (value) => onPropertyUpdate('borderWidth', value),
        'px'
      )}
      {renderSlider(
        'Border Radius',
        selectedElement.properties.borderRadius,
        0,
        50,
        1,
        (value) => onPropertyUpdate('borderRadius', value),
        'px'
      )}
    </>
  );

  const renderLayoutProperties = () => (
    <>
      <Text style={{
        fontSize: 16,
        fontWeight: 'bold',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 12
      }}>
        Position
      </Text>

      <XStack gap="$3" marginBottom="$3">
        <View style={{ flex: 1 }}>
          {renderNumberInput(
            'X Position',
            selectedElement.x,
            (value) => onPositionUpdate(value, selectedElement.y),
            'px'
          )}
        </View>
        <View style={{ flex: 1 }}>
          {renderNumberInput(
            'Y Position',
            selectedElement.y,
            (value) => onPositionUpdate(selectedElement.x, value),
            'px'
          )}
        </View>
      </XStack>

      <Text style={{
        fontSize: 16,
        fontWeight: 'bold',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 12,
        marginTop: 16
      }}>
        Size
      </Text>

      <XStack gap="$3" marginBottom="$3">
        <View style={{ flex: 1 }}>
          {renderNumberInput(
            'Width',
            selectedElement.width,
            (value) => onSizeUpdate(value, selectedElement.height),
            'px',
            10
          )}
        </View>
        <View style={{ flex: 1 }}>
          {renderNumberInput(
            'Height',
            selectedElement.height,
            (value) => onSizeUpdate(selectedElement.width, value),
            'px',
            10
          )}
        </View>
      </XStack>

      <Text style={{
        fontSize: 16,
        fontWeight: 'bold',
        color: theme ? '#333333' : '#ffffff',
        marginBottom: 12,
        marginTop: 16
      }}>
        Spacing
      </Text>

      {renderSlider(
        'Padding',
        selectedElement.properties.padding,
        0,
        50,
        1,
        (value) => onPropertyUpdate('padding', value),
        'px'
      )}

      {renderSlider(
        'Margin',
        selectedElement.properties.margin,
        0,
        50,
        1,
        (value) => onPropertyUpdate('margin', value),
        'px'
      )}
    </>
  );

  const renderAdvancedProperties = () => (
    <>
      {renderTextInput(
        'Data Binding',
        selectedElement.properties.dataBinding,
        (value) => onPropertyUpdate('dataBinding', value),
        'Enter data field name...'
      )}

      <View style={{
        marginTop: 16,
        padding: 12,
        backgroundColor: theme ? '#f0f8ff' : '#1a237e',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme ? '#e3f2fd' : '#3949ab'
      }}>
        <Text style={{
          fontSize: 12,
          color: '#2196F3',
          fontWeight: '600',
          marginBottom: 4
        }}>
          Element Information
        </Text>
        <Text style={{
          fontSize: 11,
          color: theme ? '#666666' : '#cccccc'
        }}>
          ID: {selectedElement.id}
        </Text>
        <Text style={{
          fontSize: 11,
          color: theme ? '#666666' : '#cccccc'
        }}>
          Type: {selectedElement.type}
        </Text>
        <Text style={{
          fontSize: 11,
          color: theme ? '#666666' : '#cccccc'
        }}>
          Position: {Math.round(selectedElement.x)}, {Math.round(selectedElement.y)}
        </Text>
        <Text style={{
          fontSize: 11,
          color: theme ? '#666666' : '#cccccc'
        }}>
          Size: {Math.round(selectedElement.width)} × {Math.round(selectedElement.height)}
        </Text>
      </View>
    </>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralProperties();
      case 'style':
        return renderStyleProperties();
      case 'layout':
        return renderLayoutProperties();
      case 'advanced':
        return renderAdvancedProperties();
      default:
        return null;
    }
  };

  return (
    <View style={{ flex: 1 }}>
      {/* Element Header */}
      <View style={{
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e0e0e0' : '#333333',
        backgroundColor: theme ? '#f8f9fa' : '#2a2a2a'
      }}>
        <XStack alignItems="center" gap="$3">
          <View style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: '#2196F3',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MaterialIcons
              name={getElementIcon(selectedElement.type)}
              size={18}
              color="#ffffff"
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: theme ? '#333333' : '#ffffff',
              textTransform: 'capitalize'
            }}>
              {selectedElement.type} Element
            </Text>
            <Text style={{
              fontSize: 12,
              color: theme ? '#666666' : '#cccccc'
            }}>
              {selectedElement.properties.text || selectedElement.properties.placeholder || 'Element'}
            </Text>
          </View>
        </XStack>
      </View>

      {/* Section Tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{
        maxHeight: 60,
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e0e0e0' : '#333333'
      }}>
        <XStack padding="$2" gap="$1">
          {sections.map((section) => (
            <TouchableOpacity
              key={section.key}
              onPress={() => setActiveSection(section.key as any)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 6,
                backgroundColor: activeSection === section.key
                  ? (theme ? '#e3f2fd' : '#1a237e')
                  : 'transparent'
              }}
            >
              <MaterialIcons
                name={section.icon as any}
                size={16}
                color={activeSection === section.key
                  ? '#2196F3'
                  : (theme ? '#666666' : '#cccccc')
                }
                style={{ marginRight: 6 }}
              />
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: activeSection === section.key
                  ? '#2196F3'
                  : (theme ? '#666666' : '#cccccc')
              }}>
                {section.label}
              </Text>
            </TouchableOpacity>
          ))}
        </XStack>
      </ScrollView>

      {/* Section Content */}
      <ScrollView style={{ flex: 1, padding: 16 }}>
        {renderSectionContent()}
      </ScrollView>
    </View>
  );
};

const getElementIcon = (type: string) => {
  switch (type) {
    case 'text': return 'text-fields';
    case 'header': return 'title';
    case 'image': return 'image';
    case 'input': return 'input';
    case 'button': return 'smart-button';
    case 'container': return 'crop-landscape';
    case 'divider': return 'horizontal-rule';
    default: return 'widgets';
  }
};

export default PropertiesPanel;